import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Spacer } from '@ontruck/orc';

import LoadSpinner from 'components/LoadSpinner';
import ErrorMessage from 'components/ErrorMessage';
import PriceConceptsBreakdown from 'components/PriceConceptsBreakdown';
import NonFeasibleEstimationMessage from 'components/NonFeasibleEstimationMessage';
import NonFeasibleMessage from 'components/summary/NonFeasibleMessage';
import { useFeasibility } from 'hooks/useFeasibility';
import TotalPrice from 'components/Price/TotalPrice';
import LiteralCurrency from 'components/LiteralCurrency';
import { useTranslation } from 'react-i18next';

const SummaryPrice = ({
  className,
  title,
  quote,
  concepts,
  totalPrices,
  hasError,
  isFetching,
}) => {
  const { t } = useTranslation('Quote');
  const { showFeasibilityMessage } = useFeasibility();
  const showFeasibilityEstimationMessage = useMemo(
    () =>
      showFeasibilityMessage && typeof quote?.net_full_price !== 'undefined',
    [showFeasibilityMessage, quote]
  );

  return (
    <div className={className} data-testid="summary-price">
      {title && <h1>{title}</h1>}

      {hasError && <ErrorMessage t={t} shouldDiscard />}

      {isFetching ? (
        <Spacer mBottom="M">
          <LoadSpinner
            loadText={t('load_text')}
            loadTitle={t('load_title')}
            skin="standard"
          />
        </Spacer>
      ) : (
        <>
          {showFeasibilityMessage && (
            <div className="feasibility-message-container">
              {showFeasibilityEstimationMessage && (
                <NonFeasibleEstimationMessage price={quote?.net_full_price} />
              )}
              <NonFeasibleMessage />
            </div>
          )}

          {!showFeasibilityMessage && (
            <>
              <div className="concepts">
                <PriceConceptsBreakdown concepts={concepts} showTooltips />
              </div>
              {totalPrices.map((totalPrice, i) => (
                <div className="total-container" key={i}>
                  <TotalPrice
                    priceTitle={totalPrice.title}
                    totalPrice={totalPrice.netPrice}
                    collapsable={false}
                    t={t}
                  />

                  <div className="vat-value">
                    <LiteralCurrency value={totalPrice.fullPrice} />{' '}
                    {t('including_vat')}
                  </div>
                </div>
              ))}
              {totalPrices.length > 1 && (
                <div className="price-clarifications">
                  <p>{t('multiple_prices_clarification')}</p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

SummaryPrice.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  quote: PropTypes.object,
  hasError: PropTypes.bool,
  isFetching: PropTypes.bool,
  concepts: PropTypes.shape({}),
  totalPrices: PropTypes.array,
};

SummaryPrice.defaultProps = {
  className: '',
  title: '',
  concepts: [],
  totalPrices: [
    {
      title: 'QUOTE 1',
      netPrice: 34.11,
      fullPrice: 42.11,
    },
    {
      title: 'QUOTE 2',
      netPrice: 44.11,
      fullPrice: 62.11,
    },
  ],
  hasError: false,
  isFetching: false,
};

export default SummaryPrice;
